"use client";

import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { NotificationsPanel } from "@/components/doc/left-sidebar/top-menu/notifications/NotificationsPanel";
import {
  TOP_MENU_BUTTON_CLASS_NAME,
  TOP_MENU_BUTTON_OPEN_CLASS_NAME,
} from "@/components/doc/left-sidebar/top-menu/topMenuButtonStyles";
import { UnreadCountBadge } from "@/components/doc/left-sidebar/top-menu/UnreadCountBadge";
import { Icon } from "@/components/icons/Icon";
import { Sidebar } from "@/components/sidebar";
import { useStore } from "@/store";
import { twMerge } from "@/utils/twMerge";

const NotificationsPopoverInner = observer(function NotificationsPopoverInner({
  open,
  collapsed,
}: {
  open: boolean;
  collapsed: boolean;
}) {
  const { notifications } = useStore();
  const t = useTranslations();
  const unread = notifications.unreadCount;

  return (
    <>
      <PopoverButton
        as={Sidebar.Button}
        icon={
          <span className="relative flex">
            <Icon
              name="NotificationsLarge"
              className={open ? "[&>path]:stroke-blue-500" : undefined}
            />
            <UnreadCountBadge count={unread} label={`${unread} unread notifications`} />
          </span>
        }
        title={t("sidebar.notifications.title")}
        disableTooltip={open}
        hintKey="notifications"
        className={twMerge(
          !collapsed && TOP_MENU_BUTTON_CLASS_NAME,
          open && TOP_MENU_BUTTON_OPEN_CLASS_NAME,
        )}
      />
      <PopoverPanel
        portal
        anchor={
          collapsed
            ? { to: "right start", gap: 14, padding: 10 }
            : { to: "bottom start", gap: 8, padding: 10 }
        }
        className={twMerge(
          "z-100 flex min-h-0 flex-col overflow-hidden rounded-14",
          "outline-none",
          "w-[440px] max-h-[min(70vh,calc(100vh-3rem))]",
          "bg-white dark:bg-gray-900 shadow-md ring-1 ring-black/5",
        )}
      >
        <NotificationsPanel open={open} />
      </PopoverPanel>
    </>
  );
});

export const NotificationsPopover = observer(function NotificationsPopover({
  collapsed = false,
}: {
  /** Rendered in the collapsed rail rather than a full-width top menu row */
  collapsed?: boolean;
}) {
  return (
    <Popover className="relative flex">
      {({ open }) => <NotificationsPopoverInner open={open} collapsed={collapsed} />}
    </Popover>
  );
});
